import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import api from '../../../api/api';
import { cropBannerImage } from '../../../utils/bannerCrop';

export default function BannerFeedPicker({
  value,
  onChange,
  onSelect,
  onFileChange,
  onBusyChange,
  onCropStart,
}) {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [cropVersion, setCropVersion] = useState(0);
  const changeRef = useRef(onFileChange);
  changeRef.current = onFileChange;
  useEffect(() => {
    let live = true;
    setLoading(true);
    setItems([]);
    const timer = setTimeout(async () => {
      try {
        const response = await api.getAuth('/admin/banners/feed-candidates', {
          params: { query, page },
        });
        if (live) {
          setItems(response.data.data || []);
          setError('');
        }
      } catch {
        if (live) setError('피드 목록을 불러오지 못했습니다. 검색어를 다시 입력해 재시도해주세요.');
      } finally {
        if (live) setLoading(false);
      }
    }, 250);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [query, page]);
  useEffect(() => {
    if (!cropVersion || !value.feedPostId || !value.feedImageSource) return undefined;
    let live = true;
    onBusyChange(true);
    onCropStart();
    const timer = setTimeout(async () => {
      try {
        const file = await cropBannerImage(
          value.feedImageSource,
          value.cropX ?? 0.5,
          value.cropY ?? 0.5,
          value.cropZoom ?? 1,
        );
        if (live) {
          changeRef.current(file);
          setError('');
        }
      } catch (e) {
        if (live) setError(e.message);
      } finally {
        if (live) onBusyChange(false);
      }
    }, 180);
    return () => {
      live = false;
      clearTimeout(timer);
      onBusyChange(false);
    };
  }, [
    cropVersion,
    value.feedImageSource,
    value.feedPostId,
    value.cropX,
    value.cropY,
    value.cropZoom,
    onBusyChange,
    onCropStart,
  ]);
  const cropChange = (key, next) => {
    onChange({ ...value, [key]: next });
    setCropVersion((version) => version + 1);
  };
  return (
    <Stack spacing={2}>
      <Typography variant="h6">피드에서 선택</Typography>
      <Alert severity="info">
        공개된 피드의 제목과 공개 영역 요약만 가져옵니다. 선택 후 아래에서 크롭과 문구를 확인하세요.
        제목 1줄·설명 최대 2줄입니다.
      </Alert>
      <TextField
        label="연결할 피드 검색"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setPage(1);
        }}
      />
      {error && <Alert severity="error">{error}</Alert>}
      {loading ? (
        <Typography>불러오는 중…</Typography>
      ) : (
        items.map(({ ko, en }) => (
          <Button
            key={ko.id}
            variant={value.feedPostId === ko.id ? 'contained' : 'outlined'}
            disabled={!ko.slug}
            onClick={() => {
              onSelect({ ko, en });
              setCropVersion((version) => version + 1);
            }}
          >
            {ko.title}
            {value.feedPostId === ko.id ? ' · 연결됨' : ''}
          </Button>
        ))
      )}
      {!loading && !items.length && <Typography>검색 결과가 없습니다.</Typography>}
      <Stack direction="row" spacing={1}>
        <Button disabled={loading || page === 1} onClick={() => setPage(page - 1)}>
          이전
        </Button>
        <Typography>{page} 페이지</Typography>
        <Button disabled={loading || items.length < 20} onClick={() => setPage(page + 1)}>
          다음
        </Button>
      </Stack>
      {value.feedPostId && (
        <>
          <Typography>
            연결된 피드 #{value.feedPostId} · {value.feedSlug}
          </Typography>
          <Button
            onClick={() => {
              onChange({ ...value, feedPostId: null, feedSlug: null, feedImageSource: '' });
              setCropVersion(0);
            }}
          >
            피드 연결 해제 (현재 이미지·문구 유지)
          </Button>
          <FormControlLabel
            label="피드의 최신 공개 제목·요약을 자동 반영"
            control={
              <Checkbox
                checked={value.feedCopyMode !== 'manual'}
                onChange={(e) =>
                  onChange({ ...value, feedCopyMode: e.target.checked ? 'auto' : 'manual' })
                }
              />
            }
          />
          <Alert severity="info">
            문구를 직접 고치면 자동 연동이 해제됩니다. 이미지는 저장한 크롭을 유지하며, 원본 변경
            후에는 게시물을 다시 선택해 갱신하세요. 숨김·삭제한 게시물은 홈에서 제외됩니다.
          </Alert>
          {[
            ['cropX', '이미지 좌우 위치', 0, 1, 0.01, 0.5],
            ['cropY', '이미지 상하 위치', 0, 1, 0.01, 0.5],
            ['cropZoom', '이미지 확대', 1, 3, 0.05, 1],
          ].map(([key, label, min, max, step, fallback]) => (
            <Stack key={key}>
              <Typography>{label}</Typography>
              <input
                type="range"
                aria-label={label}
                value={value[key] ?? fallback}
                min={min}
                max={max}
                step={step}
                style={{ width: '100%', accentColor: '#f16438' }}
                onChange={(e) => cropChange(key, Number(e.target.value))}
              />
            </Stack>
          ))}
          <Button onClick={() => setCropVersion((version) => version + 1)}>
            현재 크롭 다시 적용
          </Button>
        </>
      )}
    </Stack>
  );
}
